import {
  CountryWithStateModel,
  CustomPricingInputModel,
  GetProductAttributesResponseModel,
  ItemDetailInputModel,
  MediaModel,
  PackagePricingInputDataModel,
  PackagePricingInputModel,
  PriceTierInputData,
  PriceTierInputOptions,
  ProductAttributeInputModel,
  ProductAttributeMappingsInput,
  ProductAttributesModel,
  ProductAttributeWithOption,
  ProductInventorySettingsInputModel,
  ProductPricingPageInputModel,
  ProductReOrderSettingsInputModel,
  RelatedSectionModel,
  StandardPricingInputModel,
} from "../../shared/models";
import { AuthService, UtilityMethods } from "@mindmemobile/mm-shared-global";
import { action, computed, makeAutoObservable, reaction } from "mobx";
import { Subject } from "rxjs";
import {
  ItemBillingType,
  PricingModel,
  UpsertProductActiveStep,
} from "../../shared/enums";
import {
  ProductService,
  RelatedItemsService,
} from "../../shared/services";
import {
  IAPICreateUpdatePriceModel,
  IAPICreateUpdateProductModel,
  IAPIGetEditProductModel,
  IAPIItemOptionsRequest,
  IAPIPriceTermSettings,
} from "../../shared/interfaces";

export default class ProductStore {
  public attributeData: GetProductAttributesResponseModel[] = [];
  public detailPage: ItemDetailInputModel;
  public volumePricing: PriceTierInputData;
  public tieredPricing: PriceTierInputData;
  public packagePricing: PackagePricingInputModel;
  public customPricing: CustomPricingInputModel = new CustomPricingInputModel();
  public standardPricing: StandardPricingInputModel =
    new StandardPricingInputModel();
  public attributeInputs: ProductAttributeInputModel =
    new ProductAttributeInputModel();
  public activeStep: UpsertProductActiveStep;
  public headerCaption: string;
  public selectedAttributes: ProductAttributesModel[] = [];
  readonly $reset = new Subject<boolean>();
  public showAddAttribute: boolean = false;

  public medias: MediaModel[];
  public showLoader: boolean;
  public apiBinding: boolean;
  public editAttributeIndex: number;
  public realtedItemService: RelatedItemsService;

  public pricingPage: ProductPricingPageInputModel =
    new ProductPricingPageInputModel();
  public isSaveUpdateInProgress: boolean = false;
  // public countriesWithState: CountryWithStateModel[] = [];
  //public countryStore: CountryStore;

  @computed
  private getInitialSate() {
    return {
      headerCaption: "Product Details",
      // pricingPageForm: this.getPricingFormControl(ProductInputs.getPricingPageForm()),
      volumePricing: this.defaultTiers,
      tieredPricing: this.defaultTiers,
      packagePricing: this.defaultPackagePrice,
      standardPricing: new StandardPricingInputModel(),
      activeStep: UpsertProductActiveStep.Detail,
      medias: [],
    };
  }

  constructor() {
    this.realtedItemService = new RelatedItemsService();
    const initialSate = this.getInitialSate();
    this.headerCaption = initialSate.headerCaption;
    // this.pricingPageForm = initialSate.pricingPageForm;
    this.detailPage = new ItemDetailInputModel();
    this.volumePricing = initialSate.volumePricing;
    this.tieredPricing = initialSate.tieredPricing;
    this.packagePricing = initialSate.packagePricing;
    this.activeStep = initialSate.activeStep;
    this.medias = initialSate.medias;
    this.showLoader = false;
    this.apiBinding = false;
    this.editAttributeIndex = -1;

    // this.countryStore = new CountryStore();
    // this.countriesWithState = this.countryStore.countriesWithState;
    // this.countryStore.onCountryDataLoaded.subscribe((data) => {
    //   this.countriesWithState = data;
    // });

    AuthService.hasAccount &&
      ProductService.getAllAttribues(AuthService.authData.accountId).subscribe({
        next: (data) => {
          this.attributeData = data;
        },
      });
    makeAutoObservable(this);
    reaction(
      () => this.activeStep,
      () => this.updateStateBasedOnActiveStatusChange()
    );

    // reaction(
    //     () => this.selectedAttributes,
    //     () => this.generateAttributeInputs(),
    // );
    reaction(
      () => this.detailPage.hasVariants,
      () => this.onVariantFlagChange()
    );
  }

  onVariantFlagChange() {
    this.pricingPage = new ProductPricingPageInputModel({
      ...this.pricingPage,
      inventorySettings: new ProductInventorySettingsInputModel({
        ...this.pricingPage.inventorySettings,
        hideAvailable: this.detailPage.hasVariants,
      }),
    });
  }

  onPricingPageChange() {
    this.attributeInputs = new ProductAttributeInputModel({
      ...this.attributeInputs,
      priceByVariant: !this.isStandardPricing()
        ? false
        : this.attributeInputs.priceByVariant,
      originalPriceByVariant: !this.isStandardPricing()
        ? false
        : this.attributeInputs.originalPriceByVariant,
      costByVariant: !this.isStandardPricing()
        ? false
        : this.attributeInputs.costByVariant,
      showOnHand: this.pricingPage.inventorySettings.isTrackInventory,
    });
  }

  @action
  toggleShowAttribute(editAttributeIndex = -1) {
    this.showAddAttribute = !this.showAddAttribute;
    this.editAttributeIndex = editAttributeIndex;
  }

  @action
  loaderFlag(show: boolean) {
    this.showLoader = show;
  }

  @action
  generateAttributeInputs(isUpdateOrDelete: boolean = false) {
    if (this.apiBinding && !isUpdateOrDelete) {
      this.apiBinding = false;
      return;
    }
    const attributeWithOptions = this.crossJoinArr(this.selectedAttributes);
    const options = attributeWithOptions.map((s) => {
      let newOption = new ProductAttributeMappingsInput({
        attributeMappings: s.map((d, idx) => {
          return new ProductAttributeWithOption({
            ...d,
            referenceId: s[idx].referenceId,
          });
        }),
      });

      const existingMatch = this.attributeInputs.options.find(
        (a) => a.referenceIds === newOption.referenceIds
      );

      if (existingMatch) {
        newOption = new ProductAttributeMappingsInput({
          ...existingMatch,
          attributeMappings: s.map((d, idx) => {
            return new ProductAttributeWithOption({
              ...d,
              referenceId: s[idx].referenceId,
            });
          }),
        });
      }
      return newOption;
    });

    this.attributeInputs = new ProductAttributeInputModel({
      ...this.attributeInputs,
      options: options,
    });

    this.onPricingPageChange();
  }

  @action
  toggleRemoveOption(optionIndex: number, remove: boolean) {
    const clonedAttributeInputs = this.attributeInputs;
    const optionToUpdate = clonedAttributeInputs.options[optionIndex];
    if (remove) {
      optionToUpdate.sku = "";
      optionToUpdate.barCode = "";
      optionToUpdate.price = "";
      optionToUpdate.originalPrice = "";
      optionToUpdate.cost = "";
      optionToUpdate.sku = "";
    }
    clonedAttributeInputs.options[optionIndex].isRemoved = remove;
    this.attributeInputs = new ProductAttributeInputModel({
      ...clonedAttributeInputs,
    });
  }

  @action updateSortedSelectedAttributes(
    sortedAttributes: ProductAttributesModel[]
  ) {
    this.selectedAttributes = sortedAttributes.map((a) => {
      return new ProductAttributesModel(a);
    });
    this.generateAttributeInputs();
  }

  @action
  updateSelectedAttributes(attribute: ProductAttributesModel) {
    if (this.editAttributeIndex > -1) {
      this.selectedAttributes[this.editAttributeIndex] = attribute;
    } else {
      attribute.id = `${new Date().getTime()}`;
      this.selectedAttributes.push(attribute);
    }
    this.selectedAttributes = this.selectedAttributes.map((a) => {
      return new ProductAttributesModel(a);
    });
    this.generateAttributeInputs(true);
  }

  @action
  deleteSelectedAttribute(idx: number) {
    const clonedArray = this.selectedAttributes || [];
    clonedArray.splice(idx, 1);
    this.selectedAttributes = clonedArray.map((a) => {
      return new ProductAttributesModel(a);
    });
    this.generateAttributeInputs(true);
  }

  @action
  updateStateBasedOnActiveStatusChange() {
    switch (this.activeStep) {
      case UpsertProductActiveStep.Detail:
        this.headerCaption = "Product Details";
        break;
      case UpsertProductActiveStep.Pricing:
        this.headerCaption = "Product Pricing";
        break;
      case UpsertProductActiveStep.Variants:
        this.headerCaption = "Product Variants";
        break;
      case UpsertProductActiveStep.Options:
        this.headerCaption = "Product Options";
        break;
    }
  }

  @action
  updateActiveStep(activeStep: UpsertProductActiveStep) {
    this.activeStep = activeStep;
  }

  crossJoinArr = (
    attributeData: ProductAttributesModel[]
  ): ProductAttributeWithOption[][] => {
    const options: ProductAttributeWithOption[][] = [];

    attributeData.forEach((element) => {
      const result = element.options.map((d) => {
        return new ProductAttributeWithOption({
          attributeId: element.id,
          attributeName: element.name.toString(),
          optionId: d.id,
          optionName: d.name,
          type: element.attributeType,
          valueType: element.valueType,
          color: d.color || undefined,
          name: d.name,
          referenceId: d.referenceId,
        });
      });
      options.push(result);
    });
    const result: ProductAttributeWithOption[][] = [];
    if (options.length > 0) {
      this.cartesianProduct(options, result);
    }
    return result;
  };

  recursive(
    first: GetProductAttributesResponseModel,
    rest: GetProductAttributesResponseModel[]
  ) {
    const options: ProductAttributeWithOption[][] = [];
    for (let i = 0; i < first.options.length; i++) {
      const firstOption = first.options[i];
      const firstData = new ProductAttributeWithOption({
        attributeId: first.id,
        attributeName: first.name,
        optionId: firstOption.id,
        optionName: firstOption.name,
      });

      const secondOptions: ProductAttributeWithOption[][] = [];
      secondOptions.push([firstData]);
      rest.forEach((arr) => {
        for (let k = 0; k < arr.options.length; k++) { }
        const first1 =
          arr.options.length > i ? arr.options[i] : arr.options[i - 1];

        const r1 = new ProductAttributeWithOption({
          attributeId: arr.id,
          attributeName: arr.name,
          optionId: first1.id,
          optionName: first1.name,
        });
        secondOptions.push([r1]);
      });
      const result = secondOptions.reduce((a, b) => a.concat(b));
      options.push(result);
    }
    return options;
  }

  cartesianProduct(
    attrbutes: any[][],
    options: ProductAttributeWithOption[][],
    prefix = []
  ) {
    if (!attrbutes.length) {
      // Base case: no more arrays to combine, so print the current combination
      const r221 = prefix.reduce(
        (accumulator, value) => accumulator.concat(value),
        [] as ProductAttributeWithOption[]
      );
      options.push(r221);
    } else {
      // Recursive case: iterate over the first array and combine with the rest

      const [first, ...rest] = attrbutes;
      first.forEach((opt) => {
        // const r22 = prefix.reduce(
        //     (accumulator, value) => accumulator.concat(value),
        //     [] as ProductAttributeWithOption[],
        // );
        // r22.push(opt);
        // const r4 = r22.reduce((accumulator, value) => accumulator.concat(value as never), [] as never[]);

        this.cartesianProduct(rest, options, prefix.concat(opt));
      });
    }
  }

  join2Arr(
    arr1: GetProductAttributesResponseModel,
    arr2: GetProductAttributesResponseModel[],
    allArr: GetProductAttributesResponseModel[]
  ): ProductAttributeWithOption[][] {
    let dataSet: ProductAttributeWithOption[] = [];
    const dataSet1: ProductAttributeWithOption[][] = [];
    allArr.forEach((arr) => {
      dataSet = [];
      arr.options.forEach((o) => {
        const r1 = new ProductAttributeWithOption({
          attributeId: arr.id,
          attributeName: arr.name,
          optionId: o.id,
          optionName: o.name,
        });

        dataSet.push(r1);
      });
      dataSet1.push(dataSet);
    });

    const options: ProductAttributeWithOption[][] = [];
    arr1.options.forEach((option1) => {
      const option1Data = new ProductAttributeWithOption({
        optionName: `${option1.name}`,
        attributeId: arr1.id,
        attributeName: arr1.name,
        optionId: option1.id,
        groupId: `${arr1.id}_${option1.id}`,
      });

      if (arr2.length == 0) {
        options.push([option1Data]);
      }

      arr2.forEach((option2) => {
        option2.options.forEach((data) => {
          options.push([
            option1Data,
            new ProductAttributeWithOption({
              optionName: `${data.name}`,
              attributeId: option2.id,
              attributeName: option2.name,
              optionId: data.id,
              groupId: `${arr1.id}_${option1.id}`,
            }),
          ]);
        });
      });
    });
    return options;
  }

  get defaultTiers(): PriceTierInputData {
    return new PriceTierInputData({
      options: [
        new PriceTierInputOptions({
          startValue: 1,
          endValue: "1",
          discountPercentage: "0",
        }),
        new PriceTierInputOptions({ startValue: 2, endValue: "∞" }),
      ],
    });
  }

  get defaultPackagePrice(): PackagePricingInputModel {
    return new PackagePricingInputModel({
      showCost: false,
      showOriginalPrice: false,
      options: [new PackagePricingInputDataModel()],
    });
  }

  // @computed
  // getPricingFormControl(newFields: InputFieldModel[]): InputFormControl {
  //     return new InputFormControl({
  //         fields: newFields,
  //         onError: pipe((fields: InputFieldModel[]) => this.onErrorPricingPage(fields)),
  //         onSuccess: pipe(() => this.onPricingPageSuccess()),
  //     });
  // }

  // @computed
  // getDetailPageFormControl(newFields: InputFieldModel[]): InputFormControl {
  //     return new InputFormControl({
  //         fields: newFields,
  //         onError: pipe((fields: InputFieldModel[]) => this.onErrorDetailPage(fields)),
  //         onSuccess: pipe((fields: InputFieldModel[]) => this.onDetailPageSuccess(fields)),
  //     });
  // }

  // @action
  // updatePricingField(field: InputFieldModel): void {
  //     const fields = this.pricingPageForm.fields.map((a) => Object.assign({}, a));
  //     const index = fields.findIndex((x) => x.dataKey == field.dataKey);
  //     if (index > -1) {
  //         fields[index] = field;
  //         const updatePricingModel =
  //             field.dataKey === ProductPricingPageDataKeys.PricingModel &&
  //             fields.find((x) => x.dataKey === ProductPricingPageDataKeys.PricingModel)?.value !== field?.value;

  //         if (updatePricingModel) {
  //             this.setDefaultPricingModel();
  //         }
  //         this.pricingPageForm = this.getPricingFormControl(fields);
  //         this.onPricingPageChange();
  //     }
  // }

  // @action
  // onErrorPricingPage(fields: InputFieldModel[]) {
  //     const clonedFields = fields.map((a) => Object.assign({}, a));
  //     this.pricingPageForm = this.getPricingFormControl(clonedFields);
  //     this.validatePricingModel();
  // }

  // @action
  // public onPricingPageSuccess(): void {
  //     this.validatePricingModel();
  // }

  @action
  validatePricingPage() {
    // const submitSuccess = this.pricingPageForm.submit();
    // const pricingSuccess = this.validatePricingModel();
    // return submitSuccess && pricingSuccess;

    const pricingSuccess = this.pricingPage.validate();

    this.pricingPage = new ProductPricingPageInputModel({
      ...this.pricingPage,
    });
    return pricingSuccess;
  }

  @action
  validateVariantsPage(): boolean {
    return this.validateAttributes().length === 0;
  }

  @action
  validate(step?: UpsertProductActiveStep): boolean {
    let isValid = true;
    const isTabChange = step ? true : false;
    const stepToValidate = isTabChange ? step : this.activeStep;
    switch (stepToValidate) {
      case UpsertProductActiveStep.Detail:
        isValid = this.detailPage.validate();
        this.detailPage = new ItemDetailInputModel({
          ...this.detailPage,
        });
        break;
      case UpsertProductActiveStep.Pricing:
        isValid = this.validatePricingPage();
        this.pricingPage = new ProductPricingPageInputModel({
          ...this.pricingPage,
        });
        break;
      case UpsertProductActiveStep.Variants:
        isValid = this.validateVariantsPage();
        break;
      case UpsertProductActiveStep.Options:
        isValid = this.validateOptionsPage();
        break;
      default:
        return false;
    }
    return isValid;
  }

  @action
  validatePreviousSteps(currentStep: UpsertProductActiveStep): {
    isValid: boolean;
    errorStep?: UpsertProductActiveStep;
  } {
    const steps = [
      UpsertProductActiveStep.Detail,
      UpsertProductActiveStep.Pricing,
      UpsertProductActiveStep.Variants,
      UpsertProductActiveStep.Options,
    ];
    const currentStepIndex = steps.indexOf(currentStep);
    for (let i = 0; i < currentStepIndex; i++) {
      const step = steps[i];
      if (
        step === UpsertProductActiveStep.Variants &&
        !this.detailPage.hasVariants
      ) {
        continue;
      }
      if (!this.validate(step)) {
        return { isValid: false, errorStep: step };
      }
    }
    return { isValid: true };
  }

  @action
  updateVolumePricingTier(volumePricing: PriceTierInputData) {
    this.volumePricing = new PriceTierInputData({
      ...volumePricing,
    });
  }
  @action
  updateTieredPricingTier(tieredPricing: PriceTierInputData) {
    this.tieredPricing = new PriceTierInputData({
      ...tieredPricing,
    });
  }

  @action
  validateVolumePricing(): boolean {
    this.volumePricing.validate();
    this.volumePricing = new PriceTierInputData({
      ...this.volumePricing,
    });
    return (
      this.volumePricing.errors.length === 0 &&
      this.volumePricing.tierErrorIndexes.length === 0
    );
  }
  @action
  validateTieredPricing(): boolean {
    this.tieredPricing.validate();
    this.tieredPricing = new PriceTierInputData({
      ...this.tieredPricing,
    });
    return (
      this.tieredPricing.errors.length === 0 &&
      this.tieredPricing.tierErrorIndexes.length === 0
    );
  }

  @action
  addPackagePricing() {
    const fields = this.packagePricing.options.map((a) => Object.assign({}, a));

    fields.push(new PackagePricingInputDataModel());
    this.packagePricing = new PackagePricingInputModel({
      ...this.packagePricing,
      options: fields,
    });
  }

  @action
  deletePackagePricing(idx: number) {
    const fields = this.packagePricing.options.map((a) => Object.assign({}, a));
    fields.splice(idx, 1);
    this.packagePricing = new PackagePricingInputModel({
      ...this.packagePricing,
      options: fields,
    });
  }

  @action
  updatePackagePricing(data: PackagePricingInputModel) {
    this.packagePricing = data;
  }

  // @action
  // validatePricingModel(): boolean {
  //     const field = this.pricingPageForm.findInputField(ProductPricingPageDataKeys.PricingModel);

  //     switch (field?.dataKey) {
  //         case ProductPricingPageDataKeys.PricingModel:
  //             switch (PricingModel[field.value as keyof typeof PricingModel]) {
  //                 case PricingModel.Volume:
  //                     return this.validateVolumePricing();
  //                 case PricingModel.Tiered:
  //                     return this.validateTieredPricing();
  //                 case PricingModel.Package:
  //                     this.packagePricing.validate();
  //                     this.packagePricing = new PackagePricingInputModel({
  //                         ...this.packagePricing,
  //                     });
  //                     return this.packagePricing?.errors?.length === 0;
  //                 case PricingModel.Custom:
  //                     this.customPricing.validate(),
  //                         (this.customPricing = new CustomPricingInputModel({
  //                             ...this.customPricing,
  //                         }));
  //                     return this.customPricing.errors.length === 0;
  //                 case PricingModel.Standard:
  //                     this.standardPricing.validate(),
  //                         (this.standardPricing = new StandardPricingInputModel({
  //                             ...this.standardPricing,
  //                         }));
  //                     return this.standardPricing.errors.length === 0;
  //             }
  //     }

  //     return false;
  // }

  // @action
  // setDefaultPricingModel() {
  //     const field = this.pricingPageForm.findInputField(ProductPricingPageDataKeys.PricingModel);

  //     switch (field?.dataKey) {
  //         case ProductPricingPageDataKeys.PricingModel:
  //             switch (PricingModel[field.value as keyof typeof PricingModel]) {
  //                 case PricingModel.Volume:
  //                     this.volumePricing = this.defaultTiers;
  //                     break;
  //                 case PricingModel.Package:
  //                     this.packagePricing = this.defaultPackagePrice;
  //                     break;
  //                 case PricingModel.Standard:
  //                     this.standardPricing = new StandardPricingInputModel();
  //                     break;
  //                 case PricingModel.Custom:
  //                     this.customPricing = new CustomPricingInputModel();
  //                     break;
  //             }
  //             break;
  //     }
  // }

  // @action updateCustomPricing(customPricing: CustomPricingInputModel) {
  //     this.customPricing = new CustomPricingInputModel(customPricing);
  // }
  // @action
  // updateStandardPricing(standardPricing: StandardPricingInputModel) {
  //     this.standardPricing = new StandardPricingInputModel(standardPricing);
  // }

  @action
  updateAttributeInputs(attributeInputs: ProductAttributeInputModel) {
    this.attributeInputs = new ProductAttributeInputModel(attributeInputs);
  }

  @action
  validateAttributes(): any[] {
    const errors =
      this.attributeInputs.options.length === 0
        ? [
          {
            field: "Select Attributes",
            index: -13,
          },
        ]
        : this.attributeInputs.validate();
    this.attributeInputs = new ProductAttributeInputModel({
      ...this.attributeInputs,
      errors: errors,
    });
    return this.attributeInputs.errors;
  }

  @action
  reset() {
    const initialState = this.getInitialSate();
    this.realtedItemService = new RelatedItemsService();
    this.headerCaption = initialState.headerCaption;
    // this.pricingPageForm = initialState.pricingPageForm;
    this.detailPage = new ItemDetailInputModel();
    this.volumePricing = initialState.volumePricing;
    this.packagePricing = initialState.packagePricing;
    this.standardPricing = initialState.standardPricing;
    this.activeStep = initialState.activeStep;
    this.medias = initialState.medias;
    this.selectedAttributes = [];
    this.$reset.next(true);
    this.editAttributeIndex = -1;
    this.attributeInputs = new ProductAttributeInputModel();
    this.pricingPage = new ProductPricingPageInputModel();
    this.isSaveUpdateInProgress = false;
  }

  @computed
  createSaveModel(): IAPICreateUpdateProductModel {
    const pricing = [
      {
        id: UtilityMethods.isValidObjectId(this.pricingPage.id)
          ? this.pricingPage.id
          : "",
        name: this.detailPage.name,
        billingType: ItemBillingType.Term,

        //PRODUCT WILL HAVE ONLY TERMS INTERNALLY
        termSettings: {
          pricingModel: this.pricingPage.pricingModel,
          standardPricing:
            PricingModel[this.pricingPage.pricingModel] == PricingModel.Standard
              ? this.pricingPage.standardPricing.createAPIModel()
              : null,
          packagePricing:
            PricingModel[this.pricingPage.pricingModel] == PricingModel.Package
              ? this.pricingPage.packagePricing.createAPIModel()
              : null,
          tierPricing:
            PricingModel[this.pricingPage.pricingModel] == PricingModel.Tiered
              ? this.pricingPage.tieredPricing.createAPIModel()
              : PricingModel[this.pricingPage.pricingModel] ==
                PricingModel.Volume
                ? this.pricingPage.volumePricing.createAPIModel()
                : null,
          customPricing:
            PricingModel[this.pricingPage.pricingModel] == PricingModel.Custom
              ? this.pricingPage.customPricing.createAPIModel()
              : null,
        } as IAPIPriceTermSettings,
      } as IAPICreateUpdatePriceModel,
    ];

    const variants = this.detailPage.hasVariants
      ? this.attributeInputs.createAPIModel(
        this.pricingPage.inventorySettings.isTrackInventory
      )
      : null; //const categories: IAPIOptionsSelectedDataRequest;
    const relatedItems =
      this.realtedItemService.relatedSectionData.createAPIModel();
    const request = {
      ...this.detailPage.createAPIModel(),
      accountId: AuthService.authData.accountId,
      reOrderSettings: this.pricingPage.reOrderSettings.allowReOrder
        ? this.pricingPage.reOrderSettings.createAPIModel()
        : null,
      inventorySettings: this.pricingPage.inventorySettings.createAPIModel(),
      priceCreateData: pricing,
      variants: variants,
      allowReOrder: this.pricingPage.reOrderSettings.allowReOrder,
      isTrackInventory: this.pricingPage.inventorySettings.isTrackInventory,
      options:
        relatedItems === null
          ? null
          : ({ relatedItems: relatedItems } as IAPIItemOptionsRequest),
    } as IAPICreateUpdateProductModel;

    return request;
  }

  @action updateMedia(item: MediaModel[]) {
    this.medias = item;
  }

  @action
  validateOptionsPage(): boolean {
    let isValid = true;
    if (!this.realtedItemService.relatedSectionData.validate()) {
      isValid = false;
      this.realtedItemService.relatedSectionData = new RelatedSectionModel({
        ...this.realtedItemService.relatedSectionData,
      });
    }
    return isValid;
  }

  @action
  updateDetailPage(data: ItemDetailInputModel) {
    this.detailPage = new ItemDetailInputModel(data);
  }

  @action
  constructStoreFromAPI(data: IAPIGetEditProductModel) {
    this.apiBinding = false;

    this.detailPage = ItemDetailInputModel.deserializeAPIModel(data);
    if (data.prices && data.prices?.length > 0) {
      const price = data.prices[0];
      this.pricingPage.pricingModel =
        price.termSettings?.pricingModel || PricingModel.Standard;
      this.pricingPage.id = price.id;
      if (price.termSettings) {
        switch (price.termSettings.pricingModel) {
          case PricingModel.Standard:
            if (price.termSettings?.standardPricing) {
              this.pricingPage.standardPricing =
                StandardPricingInputModel.desrializeAPI(
                  price.termSettings.standardPricing,
                  false
                );
            }
            break;
          case PricingModel.Package:
            if (price.termSettings?.packagePricing) {
              this.pricingPage.packagePricing =
                PackagePricingInputModel.desrializeAPI(
                  price.termSettings.packagePricing
                );
            }
            break;
          case PricingModel.Volume:
            if (price.termSettings?.tierPricing) {
              this.pricingPage.volumePricing = PriceTierInputData.desrializeAPI(
                price.termSettings.tierPricing
              );
            }
            break;
          case PricingModel.Tiered:
            if (price.termSettings?.tierPricing) {
              this.pricingPage.tieredPricing = PriceTierInputData.desrializeAPI(
                price.termSettings.tierPricing
              );
            }
            break;
          case PricingModel.Custom:
            if (price.termSettings?.customPricing) {
              this.pricingPage.customPricing =
                CustomPricingInputModel.desrializeAPI(
                  price.termSettings.customPricing
                );
            }
            break;
        }
      }
    }

    if (data.inventorySettings) {
      this.pricingPage.inventorySettings =
        ProductInventorySettingsInputModel.deserializeAPIModel(
          data.inventorySettings,
          data.hasVariants
        );
    }

    this.pricingPage.inventorySettings.isTrackInventory = data.isTrackInventory;
    this.pricingPage.inventorySettings.hideAvailable = data.hasVariants;
    if (data.reOrderSettings) {
      this.pricingPage.reOrderSettings =
        ProductReOrderSettingsInputModel.deserializeAPIModel(
          data.reOrderSettings
        );
    }

    this.pricingPage.reOrderSettings.allowReOrder = data.allowReOrder;

    if (
      data.hasVariants &&
      data.variants &&
      data.variants.options?.length > 0
    ) {
      this.attributeInputs = ProductAttributeInputModel.deserializeAPI(
        data.variants,
        data.isTrackInventory
      );

      if (data.selectedAttributes) {
        this.selectedAttributes = data.selectedAttributes.map((a) => {
          return ProductAttributesModel.deserializeAPIModel(a);
        });
      }
    }

    if (data.options?.relatedItems) {
      this.realtedItemService.relatedSectionData =
        RelatedSectionModel.deserializeAPIModel(data.options.relatedItems);
    }
  }

  @computed
  isStandardPricing() {
    return this.pricingPage.pricingModel === PricingModel.Standard;
  }

  @action
  updatePricingPage(data: ProductPricingPageInputModel) {
    this.pricingPage = new ProductPricingPageInputModel(data);
    this.onPricingPageChange();
  }

  toggleSaveUpdateInProgress() {
    this.isSaveUpdateInProgress = !this.isSaveUpdateInProgress;
  }
}
